import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Button, FormGroup, Input, Label, Modal } from "../../components/Modal";
import SquareButton from "../../general/SquareButton";
import {
    CreateManagedService,
    ManagedServiceType,
    ScadsysAssetsConfiguration,
    ScadsysAssetsManagedService as ScadsysAssetsManagedServiceModel,
    UpdateManagedService,
} from "../ManagedServiceStore";

interface CreateScadsysAssetsProps {
    organizationId: string;
    onDone: () => void;
}

interface ScadsysAssetsInstanceProps {
    managedService: ScadsysAssetsManagedServiceModel;
    onChange: Function;
}

const emptyConfiguration = (): ScadsysAssetsConfiguration => ({
    assetEntityTypeID: "",
    assetCategoryScopeTypeID: "",
});

const getErrorMessages = (error: any): string[] => {
    const errors = error?.response?.data?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
        return errors.map((item) => item.message || String(item));
    }

    return [error?.response?.data?.message || error?.message || "Unable to save the Scadsys Assets configuration."];
};

const ConfigurationFields = ({
    configuration,
    onChange,
    disabled = false,
}: {
    configuration: ScadsysAssetsConfiguration;
    onChange: (key: keyof ScadsysAssetsConfiguration) => (event: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
}) => (
    <div className="space-y-4">
        <FormGroup>
            <Label htmlFor="assetEntityTypeID">Asset Entity Type ID</Label>
            <Input
                id="assetEntityTypeID"
                type="text"
                value={configuration.assetEntityTypeID}
                onChange={onChange("assetEntityTypeID")}
                placeholder="Enter the entity type ID used for assets"
                disabled={disabled}
            />
            <p className="mt-1 text-xs text-gray-500">The entity type that stores individual asset records.</p>
        </FormGroup>

        <FormGroup>
            <Label htmlFor="assetCategoryScopeTypeID">Asset Category Scope Type ID</Label>
            <Input
                id="assetCategoryScopeTypeID"
                type="text"
                value={configuration.assetCategoryScopeTypeID}
                onChange={onChange("assetCategoryScopeTypeID")}
                placeholder="Enter the scope type ID used for asset categories"
                disabled={disabled}
            />
            <p className="mt-1 text-xs text-gray-500">The scope type used to organize and restrict asset categories.</p>
        </FormGroup>
    </div>
);

const ErrorMessages = ({ messages }: { messages: string[] }) =>
    messages.length > 0 ? (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
            {messages.map((message, index) => (
                <p key={index} className="text-sm text-red-800">
                    {message}
                </p>
            ))}
        </div>
    ) : null;

export const ScadsysAssetsManagedService: React.FC<CreateScadsysAssetsProps> = ({ organizationId, onDone }) => {
    const [showModal, setShowModal] = React.useState(false);
    const [configuration, setConfiguration] = React.useState<ScadsysAssetsConfiguration>(emptyConfiguration());
    const [errors, setErrors] = React.useState<string[]>([]);
    const [isSaving, setIsSaving] = React.useState(false);

    const start = () => {
        setConfiguration(emptyConfiguration());
        setErrors([]);
        setShowModal(true);
    };

    const setConfig = (key: keyof ScadsysAssetsConfiguration) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setConfiguration((current) => ({ ...current, [key]: event.target.value }));
    };

    const complete = async () => {
        const normalizedConfiguration = {
            assetEntityTypeID: configuration.assetEntityTypeID.trim(),
            assetCategoryScopeTypeID: configuration.assetCategoryScopeTypeID.trim(),
        };

        if (!normalizedConfiguration.assetEntityTypeID || !normalizedConfiguration.assetCategoryScopeTypeID) {
            setErrors(["Asset Entity Type ID and Asset Category Scope Type ID are required."]);
            return;
        }

        setIsSaving(true);
        setErrors([]);

        try {
            await CreateManagedService<ScadsysAssetsConfiguration>(organizationId, {
                type: ManagedServiceType.ScadsysAssets,
                organizationID: organizationId,
                configuration: normalizedConfiguration,
            });
            setShowModal(false);
            onDone();
        } catch (error) {
            setErrors(getErrorMessages(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <SquareButton className="col" onClick={start}>
                Scadsys Assets
                <br />
                <FontAwesomeIcon icon={["fad", "circle-plus"]} size="lg" className="text-indigo-600" />
            </SquareButton>

            <Modal
                isOpen={showModal}
                onClose={() => !isSaving && setShowModal(false)}
                title="New Scadsys Assets Managed Service"
                icon={["fad", "layer-group"]}
                iconColor="text-indigo-600"
                size="lg"
            >
                <div className="p-6">
                    <div className="mb-5 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                        <p className="text-sm text-indigo-800">
                            Connect Scadsys Assets to the entity and category scope types configured for this organization.
                        </p>
                    </div>
                    <ErrorMessages messages={errors} />
                    <ConfigurationFields configuration={configuration} onChange={setConfig} disabled={isSaving} />
                </div>
                <div className="flex justify-end space-x-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={complete} disabled={isSaving}>
                        {isSaving && <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />}
                        {isSaving ? "Creating..." : "Create"}
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export const RenderScadsysAssetsRow = (configuration: ScadsysAssetsConfiguration) => (
    <div className="space-y-1">
        <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={["fad", "layer-group"]} className="text-indigo-500" />
            <span className="font-medium">Entity type: {configuration?.assetEntityTypeID || "Not configured"}</span>
        </div>
        <div className="text-sm text-gray-500">Category scope: {configuration?.assetCategoryScopeTypeID || "Not configured"}</div>
    </div>
);

export const RenderScadsysAssetsInstance: React.FC<ScadsysAssetsInstanceProps> = ({ managedService, onChange }) => {
    const [configuration, setConfiguration] = React.useState<ScadsysAssetsConfiguration>({
        assetEntityTypeID: managedService.configuration?.assetEntityTypeID || "",
        assetCategoryScopeTypeID: managedService.configuration?.assetCategoryScopeTypeID || "",
    });
    const [errors, setErrors] = React.useState<string[]>([]);
    const [isSaving, setIsSaving] = React.useState(false);

    React.useEffect(() => {
        setConfiguration({
            assetEntityTypeID: managedService.configuration?.assetEntityTypeID || "",
            assetCategoryScopeTypeID: managedService.configuration?.assetCategoryScopeTypeID || "",
        });
    }, [managedService.id]);

    const setConfig = (key: keyof ScadsysAssetsConfiguration) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setConfiguration((current) => ({ ...current, [key]: event.target.value }));
    };

    const save = async () => {
        const normalizedConfiguration = {
            assetEntityTypeID: configuration.assetEntityTypeID.trim(),
            assetCategoryScopeTypeID: configuration.assetCategoryScopeTypeID.trim(),
        };

        if (!normalizedConfiguration.assetEntityTypeID || !normalizedConfiguration.assetCategoryScopeTypeID) {
            setErrors(["Asset Entity Type ID and Asset Category Scope Type ID are required."]);
            return;
        }

        setIsSaving(true);
        setErrors([]);

        try {
            await UpdateManagedService<ScadsysAssetsConfiguration>(managedService.organizationID, {
                ...managedService,
                configuration: normalizedConfiguration,
            });
            onChange && onChange();
        } catch (error) {
            setErrors(getErrorMessages(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="mb-6">
            <div className="mb-4 flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-400 to-indigo-600">
                    <FontAwesomeIcon icon={["fad", "layer-group"]} className="text-lg text-white" />
                </div>
                <div>
                    <h4 className="text-lg font-semibold text-gray-900">Scadsys Assets</h4>
                    <p className="text-sm text-gray-500">Asset entity and category configuration</p>
                </div>
            </div>

            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <ErrorMessages messages={errors} />
                <ConfigurationFields configuration={configuration} onChange={setConfig} disabled={isSaving} />
            </div>

            <div className="flex justify-end">
                <Button variant="primary" onClick={save} disabled={isSaving}>
                    {isSaving && <FontAwesomeIcon icon={["fad", "spinner"]} spin className="mr-2" />}
                    {isSaving ? "Saving..." : "Save Configuration"}
                </Button>
            </div>
        </div>
    );
};
